import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  List,
  ListItemButton,
  ListItemText,
  Paper,
  TextField,
  Typography,
  Button
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import { myApi } from "../../config/axiosConfig";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell as RechartsCell,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { useLocation, useNavigate } from "react-router-dom";

const TARGET_MPG = 7;

const LITERS_PER_GALLON = 3.78541;
const KM_PER_MILE = 1.60934;

function convertFuel(value, unitSystem) {
  const num = Number(value || 0);
  return unitSystem === "metric" ? num * LITERS_PER_GALLON : num;
}

function convertDistance(value, unitSystem) {
  const num = Number(value || 0);
  return unitSystem === "metric" ? num * KM_PER_MILE : num;
}

function convertEfficiencyFromMpg(value, unitSystem) {
  const num = Number(value || 0);
  return unitSystem === "metric" ? num * 0.425144 : num;
}

function fuelLabel(unitSystem) {
  return unitSystem === "metric" ? "L" : "Gal";
}

function distanceLabel(unitSystem) {
  return unitSystem === "metric" ? "KM" : "Miles";
}

function efficiencyLabel(unitSystem) {
  return unitSystem === "metric" ? "KM/L" : "MPG";
}

const n2 = new Intl.NumberFormat("en-US", {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

const n1 = new Intl.NumberFormat("en-US", {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

const n0 = new Intl.NumberFormat("en-US", {
  minimumFractionDigits: 0,
  maximumFractionDigits: 0,
});

function toNumber(value) {
  const n = Number(value);
  return Number.isFinite(n) ? n : 0;
}

function getFuelEffColor(value) {
  const num = Number(value || 0);
  if (num < 6) return "#f4cccc";
  if (num < 7) return "#efe3a1";
  return "#b6e3b6";
}

const statusStyles = {
  INFO:  { bg: "#E7C56A", color: "#5C4300" },
  ACK:   { bg: "#E7C56A", color: "#5C4300" },
  SIGN:  { bg: "#D5B25C", color: "#4A3400" },

  W1:    { bg: "#C9B0E8", color: "#5B3E87" },
  W2:    { bg: "#B999E0", color: "#56357F" },
  W3:    { bg: "#A97DD8", color: "#FFFFFF" },

  IMP:   { bg: "#E8A977", color: "#6A340C" },
  MON:   { bg: "#E8A977", color: "#6A340C" },
  TRN:   { bg: "#E8A977", color: "#6A340C" },

  FNL:   { bg: "#F28B82", color: "#7A1C16" },
  RCMD:  { bg: "#EF5350", color: "#FFFFFF" },
  TERM:  { bg: "#C62828", color: "#FFFFFF" },

  "1.IMP": { bg: "#A5D6A7", color: "#1B5E20" },
  "2.EXC": { bg: "#81C784", color: "#1B5E20" },
  "3.CLR": { bg: "#2E7D32", color: "#FFFFFF" },
};

function getStatusCode(value) {
  const text = String(value || "").trim();

  if (text.startsWith("1.IMP") || text.startsWith("1. IMP")) return "1.IMP";
  if (text.startsWith("2.EXC") || text.startsWith("2. EXC")) return "2.EXC";
  if (text.startsWith("3.CLR") || text.startsWith("3. CLR")) return "3.CLR";

  return text.split(" ")[0];
}

function statusBg(value) {
  const code = getStatusCode(value);
  return statusStyles[code]?.bg || "transparent";
}

function statusColor(value) {
  const code = getStatusCode(value);
  return statusStyles[code]?.color || "#333";
}

const statusOptions = [
  { value: "", label: "" },
  { value: "INFO", label: "INFO - Conversación informativa" },
  { value: "ACK", label: "ACK - Reconoció resultados" },
  { value: "SIGN", label: "SIGN - Firmó PDF" },
  { value: "W1", label: "W1 - Primera advertencia" },
  { value: "W2", label: "W2 - Segunda advertencia" },
  { value: "W3", label: "W3 - Tercera advertencia" },
  { value: "IMP", label: "IMP - En plan de mejora" },
  { value: "MON", label: "MON - En monitoreo" },
  { value: "TRN", label: "TRN - Reentrenamiento" },
  { value: "FNL", label: "FNL - Advertencia final" },
  { value: "RCMD", label: "RCMD - Recomendado reemplazo" },
  { value: "TERM", label: "TERM - Terminado" },
  { value: "1.IMP", label: "1. IMP - Mejora confirmada" },
  { value: "2.EXC", label: "2. EXC - Sobresaliente" },
  { value: "3.CLR", label: "3. CLR - Caso cerrado" },
];

const statusMap = statusOptions.reduce((acc, item) => {
  acc[item.value] = item.label;
  return acc;
}, {});

function getFullStatusLabel(status) {
  return statusMap[status || ""] || status || "";
}

function toggleSelection(value, setter) {
  setter((prev) =>
    prev.includes(value) ? prev.filter((item) => item !== value) : [...prev, value]
  );
}

function SortHeader({
  children,
  field,
  activeField,
  direction,
  onClick,
  align = "left",
  right = true,
}) {
  const isActive = activeField === field;

  return (
    <Box
      onClick={() => onClick(field)}
      sx={{
        px: 0.8,
        py: 0.55,
        fontSize: 14,
        fontWeight: 800,
        bgcolor: "#2f2f2f",
        color: "#fff",
        borderRight: right ? "1px solid #555" : "none",
        borderBottom: "2px solid #3b9cff",
        whiteSpace: "nowrap",
        cursor: "pointer",
        display: "flex",
        alignItems: "center",
        justifyContent: align === "center" ? "center" : "space-between",
      }}
    >
      <span>{children}</span>
      <span
        style={{
          fontSize: 11,
          color: "#fff",
          opacity: isActive ? 1 : 0.35,
        }}
      >
        {isActive ? (direction === "asc" ? "▲" : "▼") : "↕"}
      </span>
    </Box>
  );
}

function Cell({
  children,
  bg = "#f3f3f3",
  align = "right",
  bold = false,
  color = "#333",
  right = true,
  onClick,
  sx = {},
}) {
  return (
    <Box
      onClick={onClick}
      sx={{
        width: "100%",
        height: "100%",
        boxSizing: "border-box", // 🔥 importante
        px: 0.8,
        py: 0.52,
        fontSize: 14,
        fontWeight: bold ? 800 : 500,
        textAlign: align,
        bgcolor: bg,
        color,
        borderRight: right ? "1px solid #d0d0d0" : "none",
        borderBottom: "1px solid #d0d0d0",
        whiteSpace: "nowrap",
        lineHeight: 1.25,
        overflow: "hidden", // 🔥 ayuda
        ...sx,
      }}
    >
      {children}
    </Box>
  );
}

function buildCompanyRanking(rows = [], isUSA = false) {
  const grouped = {};

  rows.forEach((row) => {
    if (!row || row.DistanciaOdo === "INICIAL") return;
    const company = String(
      isUSA ? row?.company : row?.Asset_trsnport_line
    ).trim() || "UNKNOWN";
    const distance = toNumber(row.DistanciaOdo);
    const fuel = toNumber(row.TotalRealConsumido);
    if (distance <= 0 || fuel <= 0) return;

    if (!grouped[company]) grouped[company] = { company, distance: 0, fuel: 0 };
    grouped[company].distance += distance;
    grouped[company].fuel += fuel;
  });

  return Object.values(grouped)
    .map((item) => ({
      company: item.company,
      fuelEff: Number((item.distance / item.fuel).toFixed(2)),
    }))
    .sort((a, b) => b.fuelEff - a.fuelEff);
}

function buildMakeRanking(rows = []) {
  const grouped = {};

  rows.forEach((row) => {
    if (!row || row.DistanciaOdo === "INICIAL") return;
    const make = String(row.brand || row.Make || "UNKNOWN").trim() || "UNKNOWN";
    const distance = toNumber(row.DistanciaOdo);
    const fuel = toNumber(row.TotalRealConsumido);
    if (distance <= 0 || fuel <= 0) return;

    if (!grouped[make]) grouped[make] = { make, distance: 0, fuel: 0 };
    grouped[make].distance += distance;
    grouped[make].fuel += fuel;
  });

  return Object.values(grouped)
    .map((item) => ({
      make: item.make,
      fuelEff: Number((item.distance / item.fuel).toFixed(2)),
    }))
    .sort((a, b) => b.fuelEff - a.fuelEff);
}

function buildDriverRanking(rows = []) {
  const grouped = {};

  rows.forEach((row) => {
    if (!row?.driver || row.DistanciaOdo === "INICIAL") return;

    const driver = String(row.driver).trim();
    const mpg = toNumber(row.MpgReal);
    const status = String(row.seguimiento_estatus || "").trim();
    const rowDate = row.ultima_fecha || row.fecha_modifica || row.fecha_alta || row.date;

    if (!Number.isFinite(mpg) || mpg <= 0) return;

    if (!grouped[driver]) {
      grouped[driver] = {
        driver,
        totalMpg: 0,
        count: 0,
        latestStatus: "",
        latestStatusDate: null,
      };
    }

    grouped[driver].totalMpg += mpg;
    grouped[driver].count += 1;

    if (status) {
      const currentDate = rowDate ? new Date(rowDate).getTime() : 0;
      const savedDate = grouped[driver].latestStatusDate
        ? new Date(grouped[driver].latestStatusDate).getTime()
        : 0;

      if (!grouped[driver].latestStatus || currentDate >= savedDate) {
        grouped[driver].latestStatus = status;
        grouped[driver].latestStatusDate = rowDate;
      }
    }
  });

  return Object.values(grouped)
    .map((item) => ({
      driver: item.driver,
      fuelEff: Number((item.totalMpg / item.count).toFixed(2)),
      statusCode: item.latestStatus || "",
      statusLabel: getFullStatusLabel(item.latestStatus || ""),
    }))
    .filter((item) => item.fuelEff > 0)
    .sort((a, b) => b.fuelEff - a.fuelEff)
    .map((item, index) => ({ rank: index + 1, ...item }));
}

function buildWeekChartData(rows = []) {
  const grouped = {};

  rows.forEach((row) => {
    if (!row?.driver || row.DistanciaOdo === "INICIAL") return;

    const driver = String(row.driver).trim();
    const mpg = toNumber(row.MpgReal);
    if (mpg <= 0) return;

    if (!grouped[driver]) grouped[driver] = { driver, total: 0, count: 0 };
    grouped[driver].total += mpg;
    grouped[driver].count += 1;
  });

  return Object.values(grouped)
    .map((item) => ({
      driver: item.driver,
      fuelEff: Number((item.total / item.count).toFixed(2)),
    }))
    .sort((a, b) => b.fuelEff - a.fuelEff)
    .slice(0, 35);
}

function parseDurationToMinutes(value) {
  if (value == null || value === "") return 0;
  if (typeof value === "number") return value * 60;

  const text = String(value).trim();
  if (!text) return 0;

  if (text.includes(":")) {
    const parts = text.split(":").map((item) => Number(item || 0));
    if (parts.length === 3) {
      const [h, m, s] = parts;
      return h * 60 + m + s / 60;
    }
    if (parts.length === 2) {
      const [h, m] = parts;
      return h * 60 + m;
    }
  }

  const num = Number(text);
  return Number.isFinite(num) ? num * 60 : 0;
}

function formatMinutesToHHMM(totalMinutes) {
  const safe = Math.max(0, Math.round(totalMinutes || 0));
  const hours = Math.floor(safe / 60);
  const minutes = safe % 60;
  return `${String(hours).padStart(2, "0")}:${String(minutes).padStart(2, "0")}`;
}

function getStickyLeft(index) {
  if (index === 0) return 0;
  if (index === 1) return 140;
  if (index === 2) return 220;
  if (index === 3) return 350;
  return 0;
}

function buildGeneralTableRows(rows = []) {
  const detailRows = rows
    .filter((row) => row && row.DistanciaOdo !== "INICIAL")
    .map((row) => {
      const resetFuel = toNumber(row.galones_colombia);
      const monterreyFuel = toNumber(row.galones_monterrey);
      const gasomaxFuel = toNumber(row.galones_gasomax);
      const lovesFuel = toNumber(row.CantidadLoves || row.total_galones_loves);
      const oxxoFuel = toNumber(row.CantidadOxxo || row.total_galones_oxxo);
      const totalFuel = toNumber(row.TotalRealConsumido);
      const distance = toNumber(row.DistanciaOdo);
      const fuelEff = toNumber(row.MpgReal);
      const fuelVar = toNumber(row.Diferencia);
      const totalMinutes = parseDurationToMinutes(row.pc_total_time_traveled);
      const idleMinutes = parseDurationToMinutes(row.pc_idletime);
      const fuelVarPct = totalFuel > 0 ? (fuelVar / totalFuel) * 100 : 0;
      const idlePct = totalMinutes > 0 ? (idleMinutes / totalMinutes) * 100 : 0;

      return {
        id: row.id,
        date: row.date,
        asset: row.asset || "-",
        truck_odometer: toNumber(row.truck_odometer),
        driver: row.driver || "-",
        resetFuel,
        monterreyFuel,
        gasomaxFuel,
        oxxoFuel,
        lovesFuel,
        argFuel: toNumber(row.CantidadArguindegui || row.total_galones_arguindegui),
        totalFuel,
        distance,
        fuelEff,
        status: getFullStatusLabel(row.seguimiento_estatus || ""),
        fuelVar,
        fuelVarPct,
        idlePct,
        idleTimeMinutes: idleMinutes,
        totalTimeMinutes: totalMinutes,
      };
    });

  const totals = detailRows.reduce(
    (acc, row) => {
      acc.resetFuel += row.resetFuel;
      acc.monterreyFuel += row.monterreyFuel;
      acc.gasomaxFuel += row.gasomaxFuel;
      acc.lovesFuel += row.lovesFuel;
      acc.oxxoFuel += row.oxxoFuel;
      acc.argFuel += row.argFuel;
      acc.totalFuel += row.totalFuel;
      acc.distance += row.distance;
      acc.fuelVar += row.fuelVar;
      acc.idleTimeMinutes += row.idleTimeMinutes;
      acc.totalTimeMinutes += row.totalTimeMinutes;
      return acc;
    },
    {
      resetFuel: 0,
      monterreyFuel: 0,
      gasomaxFuel: 0,
      lovesFuel: 0,
      oxxoFuel: 0,
      argFuel: 0,
      totalFuel: 0,
      distance: 0,
      fuelVar: 0,
      idleTimeMinutes: 0,
      totalTimeMinutes: 0,
    }
  );

  const summaryRow = {
    id: "summary",
    isSummary: true,
    date: "TOTAL",
    asset: "-",
    truck_odometer: null,
    driver: `${n0.format(detailRows.length)} rows`,
    resetFuel: totals.resetFuel,
    monterreyFuel: totals.monterreyFuel,
    gasomaxFuel: totals.gasomaxFuel,
    lovesFuel: totals.lovesFuel,
    oxxoFuel: totals.oxxoFuel,
    argFuel: totals.argFuel,
    totalFuel: totals.totalFuel,
    distance: totals.distance,
    fuelEff: totals.totalFuel > 0 ? totals.distance / totals.totalFuel : 0,
    status: "",
    fuelVar: totals.fuelVar,
    fuelVarPct: totals.totalFuel > 0 ? (totals.fuelVar / totals.totalFuel) * 100 : 0,
    idlePct: totals.totalTimeMinutes > 0 ? (totals.idleTimeMinutes / totals.totalTimeMinutes) * 100 : 0,
    idleTimeMinutes: totals.idleTimeMinutes,
    totalTimeMinutes: totals.totalTimeMinutes,
  };

  return [summaryRow, ...detailRows];
}

function GeneralTable({ rows = [], unitSystem = "imperial", isUSA = false }) {
  const [sortField, setSortField] = useState("date");
  const [sortDirection, setSortDirection] = useState("desc");

  const sortedRows = useMemo(() => {
    if (!rows.length) return rows;

    const summaryRow = rows[0];
    const detailRows = [...rows.slice(1)];

    detailRows.sort((a, b) => {
      let aValue = a[sortField];
      let bValue = b[sortField];

      if (sortField === "date") {
        aValue = new Date(a.date).getTime();
        bValue = new Date(b.date).getTime();
      }

      if (typeof aValue === "number" && typeof bValue === "number") {
        return sortDirection === "asc" ? aValue - bValue : bValue - aValue;
      }

      return sortDirection === "asc"
        ? String(aValue ?? "").localeCompare(String(bValue ?? ""))
        : String(bValue ?? "").localeCompare(String(aValue ?? ""));
    });

    return [summaryRow, ...detailRows];
  }, [rows, sortField, sortDirection]);

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection((prev) => (prev === "asc" ? "desc" : "asc"));
    } else {
      setSortField(field);
      setSortDirection(field === "date" ? "desc" : "asc");
    }
  };
  const columns = [
    { key: "date", label: "Date/Time Fuel Event", width: 140, sticky: true, sortable: true },
    { key: "asset", label: "Truck #", width: 80, sticky: true, sortable: true },
    { key: "truck_odometer", label: "Truck Odometer", width: 130, sticky: true, sortable: true },
    { key: "driver", label: "Driver", width: 220, sticky: true, sortable: true },
    { key: "resetFuel", label: `Colombia`, width: 120, sortable: true },
    { key: "monterreyFuel", label: `Monterrey`, width: 140, sortable: true },
    { key: "gasomaxFuel", label: `Gasomax`, width: 130, sortable: true },
    { key: "oxxoFuel", label: `Oxxo`, width: 110, sortable: true },
    { key: "lovesFuel", label: `Loves`, width: 110, sortable: true },
    { key: "argFuel", label: `Arguindegui`, width: 140, sortable: true },
    { key: "totalFuel", label: `Total Fuel`, width: 105, bg: "#d9f2d9", sortable: true },
    { key: "distance", label: `Distance`, width: 95, bg: "#cfe2f3", sortable: true },
    { key: "fuelEff", label: efficiencyLabel(unitSystem), width: 85, bg: "#000", color: "#fff", sortable: true },
    { key: "status", label: "MPG Review Status", width: 170, sortable: true },
    { key: "fuelVar", label: "Fuel VAR", width: 90, sortable: true },
    { key: "fuelVarPct", label: "Fuel VAR %", width: 90, sortable: true },
    { key: "idlePct", label: "IDLE %", width: 80, sortable: true },
    { key: "idleTimeMinutes", label: "IDLE TIME", width: 100, sortable: true },
    { key: "totalTimeMinutes", label: "TOTAL TIME", width: 120, sortable: true },
  ];

  const visibleColumns = isUSA
  ? columns.filter(
      (col) =>
        ![
          "truck_odometer",
          "resetFuel",
          "monterreyFuel",
          "gasomaxFuel",
          "oxxoFuel",
          "lovesFuel",
          "argFuel",
          "status",
          "fuelVar",
          "fuelVarPct",
        ].includes(col.key)
    )
  : columns;

  const renderValue = (row, column) => {
    switch (column.key) {
      case "date":
        return row.isSummary ? row.date : new Date(row.date).toLocaleString("en-US", {
          month: "numeric",
          day: "numeric",
          year: "numeric",
          hour: "numeric",
          minute: "2-digit",
        });
      case "truck_odometer":
        return row.truck_odometer == null ? "-" : n1.format(row.truck_odometer);
      case "resetFuel":
      case "monterreyFuel":
      case "gasomaxFuel":
      case "oxxoFuel":
      case "lovesFuel":
      case "argFuel":
      case "totalFuel":
        return n1.format(convertFuel(row[column.key], unitSystem));

      case "distance":
        return n1.format(convertDistance(row.distance, unitSystem));

      case "fuelEff":
        return `${n2.format(convertEfficiencyFromMpg(row.fuelEff, unitSystem))} ${efficiencyLabel(unitSystem)}`;

      case "fuelVar":
        return n1.format(convertFuel(row.fuelVar, unitSystem));
      case "fuelVarPct":
      case "idlePct":
        return `${n1.format(row[column.key])}%`;
      case "idleTimeMinutes":
      case "totalTimeMinutes":
        return formatMinutesToHHMM(row[column.key]);
      default:
        return row[column.key] || "";
    }
  };

  const getBodyBg = (row, column) => {
    if (row.isSummary) return "#7b7b7b";
    if (column.key === "totalFuel") return "#d9f2d9";
    if (column.key === "distance") return "#cfe2f3";
    if (column.key === "fuelEff") return getFuelEffColor(row.fuelEff);
    if (column.key === "fuelVar") return row.fuelVar >= 0 ? "#d9f2d9" : "#f4cccc";
    if (column.key === "fuelVarPct") return row.fuelVarPct >= 0 ? "#d9f2d9" : "#f4cccc";
    if (column.key === "idlePct") return "#f3f3f3";
    if (column.key === "status") return statusBg(row.status);
    return "#f3f3f3";
  };

  return (
    <Paper sx={{ borderRadius: 0, border: "1px solid #d5d5d5", bgcolor: "#f3f3f3", minHeight: 0, overflow: "hidden" }}>
      <Box
        sx={{
          width: "100%",
          height: "100%",
          overflow: "auto",
          "&::-webkit-scrollbar": { width: 8, height: 8 },
          "&::-webkit-scrollbar-thumb": { background: "#bdbdbd", borderRadius: 8 },
          "&::-webkit-scrollbar-track": { background: "#efefef" },
        }}
      >
        <Box sx={{ minWidth: isUSA ? 900 : 1790, width: "max-content" }}>
          <Box sx={{ display: "flex", position: "sticky", top: 0, zIndex: 20 }}>
            {visibleColumns.map((column, index) => (
              <Box
                key={column.key}
                sx={{
                  width: column.width,
                  minWidth: column.width,
                  maxWidth: column.width,
                  position: column.sticky ? "sticky" : "relative",
                  left: column.sticky ? getStickyLeft(index) : "auto",
                  zIndex: column.sticky ? 25 : 20,
                  bgcolor: column.bg || "#f3f3f3",
                }}
              >
                <Cell
                  bold
                  align={index >= 4 ? "center" : "left"}
                  bg={column.bg || "#f3f3f3"}
                  color={column.color || "#222"}
                  right
                  onClick={column.sortable ? () => handleSort(column.key) : undefined}
                  sx={{ cursor: column.sortable ? "pointer" : "default" }}
                >
                  <Box sx={{ display: "flex", alignItems: "center", justifyContent: index >= 4 ? "center" : "space-between", gap: 0.5 }}>
                    <span>{column.label}</span>
                    {column.sortable ? (
                      <span style={{ fontSize: 11, opacity: sortField === column.key ? 1 : 0.35 }}>
                        {sortField === column.key ? (sortDirection === "asc" ? "▲" : "▼") : "↕"}
                      </span>
                    ) : null}
                  </Box>
                </Cell>
              </Box>
            ))}
          </Box>

          {sortedRows.map((row) => (
            <Box key={row.id} sx={{ display: "flex", alignItems: "stretch" }}>
              {visibleColumns.map((column, index) => (
                <Box
                  key={`${row.id}-${column.key}`}
                  sx={{
                    width: column.width,
                    minWidth: column.width,
                    maxWidth: column.width,
                    position: column.sticky ? "sticky" : "relative",
                    left: column.sticky ? getStickyLeft(index) : "auto",
                    zIndex: column.sticky ? 10 : 1,
                  }}
                >
                  <Cell
                    align={
                      column.key === "status"
                        ? "left"
                        : index >= 4
                        ? "right"
                        : "left"
                    }
                    bold={!!row.isSummary}
                    bg={getBodyBg(row, column)}
                    color={
                      row.isSummary
                        ? "#fff"
                        : column.key === "status"
                        ? statusColor(row.status)
                        : "#222"
                    }
                    right
                  >
                    <Box sx={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={String(renderValue(row, column))}>
                      {renderValue(row, column)}
                    </Box>
                  </Cell>
                </Box>
              ))}
            </Box>
          ))}
        </Box>
      </Box>
    </Paper>
  );
}

function RankingSection({
  titleLeft,
  titleRight = "Fuel Eff.",
  rows,
  rowKey,
  labelField,
  totalLabel = "Total",
  selectedValues,
  setSelectedValues,
  height = 160,
  columns = "1fr 105px",
  totalFuelEffOverride = null,
  unitSystem = "imperial",
}) {
  const [sortField, setSortField] = useState(labelField);
  const [sortDirection, setSortDirection] = useState(labelField === "fuelEff" ? "desc" : "asc");
  const scrollbarWidth = 8;

  const sortedRows = useMemo(() => {
    const copy = [...rows];
    copy.sort((a, b) => {
      const aValue = a[sortField];
      const bValue = b[sortField];

      if (sortField === labelField) {
        return sortDirection === "asc"
          ? String(aValue).localeCompare(String(bValue))
          : String(bValue).localeCompare(String(aValue));
      }

      return sortDirection === "asc"
        ? Number(aValue) - Number(bValue)
        : Number(bValue) - Number(aValue);
    });
    return copy;
  }, [rows, sortField, sortDirection, labelField]);

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection((prev) => (prev === "asc" ? "desc" : "asc"));
    } else {
      setSortField(field);
      setSortDirection(field === "fuelEff" ? "desc" : "asc");
    }
  };

  const totalFuelEff =
    totalFuelEffOverride != null
      ? Number(totalFuelEffOverride || 0)
      : rows.length
      ? rows.reduce((acc, item) => acc + Number(item.fuelEff || 0), 0) / rows.length
      : 0;

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        minHeight: 0,
        border: "1px solid #d5d5d5",
        bgcolor: "#f4f4f4",
      }}
    >
      <Box sx={{ display: "grid", gridTemplateColumns: columns }}>
        <SortHeader field={labelField} activeField={sortField} direction={sortDirection} onClick={handleSort}>
          {titleLeft}
        </SortHeader>
        <SortHeader field="fuelEff" activeField={sortField} direction={sortDirection} onClick={handleSort} right={false}>
          {titleRight}
        </SortHeader>
      </Box>

      <Box
        sx={{
          maxHeight: height,
          overflowY: "auto",
          overflowX: "hidden",
          minHeight: 0,
          scrollbarGutter: "stable",
          "&::-webkit-scrollbar": { width: scrollbarWidth },
          "&::-webkit-scrollbar-thumb": { background: "#bdbdbd", borderRadius: 8 },
          "&::-webkit-scrollbar-track": { background: "#efefef" },
        }}
      >
        {sortedRows.map((row) => {
          const value = row[rowKey];
          const isSelected = selectedValues.includes(value);
          return (
            <Box
              key={value}
              onClick={() => toggleSelection(value, setSelectedValues)}
              sx={{ display: "grid", gridTemplateColumns: columns, cursor: "pointer" }}
            >
              <Cell align="left" bg={isSelected ? "#dbeafe" : "#f3f3f3"}>{row[labelField]}</Cell>
              <Cell bg={isSelected ? "#c7defc" : getFuelEffColor(row.fuelEff)} right={false}>
                {n2.format(convertEfficiencyFromMpg(row.fuelEff, unitSystem))} {efficiencyLabel(unitSystem)}
              </Cell>
            </Box>
          );
        })}
      </Box>

      <Box sx={{ display: "grid", gridTemplateColumns: columns, pr: `${scrollbarWidth}px` }}>
        <Cell bold>{totalLabel}</Cell>
        <Cell bold right={false}>
          {n2.format(convertEfficiencyFromMpg(totalFuelEff, unitSystem))} {efficiencyLabel(unitSystem)}
        </Cell>
      </Box>
    </Box>
  );
}

function DriverRankingCard({ rows, selectedDrivers, setSelectedDrivers, search, setSearch, sortState, setSortState, unitSystem = "imperial", isUSA = false }) {
  const driverColumns = isUSA ? "52px 1.35fr 86px" : "52px 1.35fr 86px 1.7fr";

  const sortedRows = useMemo(() => {
    const copy = [...rows];

    copy.sort((a, b) => {
      if (sortState.field === "rank") {
        return sortState.direction === "asc" ? a.rank - b.rank : b.rank - a.rank;
      }

      if (sortState.field === "driver") {
        return sortState.direction === "asc"
          ? a.driver.localeCompare(b.driver)
          : b.driver.localeCompare(a.driver);
      }

      if (sortState.field === "fuelEff") {
        return sortState.direction === "asc" ? a.fuelEff - b.fuelEff : b.fuelEff - a.fuelEff;
      }

      if (sortState.field === "statusLabel") {
        return sortState.direction === "asc"
          ? a.statusLabel.localeCompare(b.statusLabel)
          : b.statusLabel.localeCompare(a.statusLabel);
      }

      return 0;
    });

    return copy;
  }, [rows, sortState]);

  const handleSort = (field) => {
    setSortState((prev) => ({
      field,
      direction: prev.field === field && prev.direction === "asc" ? "desc" : "asc",
    }));
  };

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
        minHeight: 0,
        border: "1px solid #d5d5d5",
        bgcolor: "#f8f8f8",
        p: 0.7,
      }}
    >
      <TextField
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        size="small"
        placeholder="Search"
        InputProps={{ startAdornment: <SearchIcon sx={{ color: "#777", mr: 1 }} /> }}
        sx={{ bgcolor: "#fff", "& .MuiOutlinedInput-root": { borderRadius: 1, height: 40 } }}
      />

      <Box
        sx={{
          mt: 0.35,
          minHeight: 0,
          overflow: "auto",
          border: "1px solid #d0d0d0",
          bgcolor: "#fff",
          scrollbarGutter: "stable",
          "&::-webkit-scrollbar": { width: 8, height: 8 },
          "&::-webkit-scrollbar-thumb": { background: "#bdbdbd", borderRadius: 8 },
          "&::-webkit-scrollbar-track": { background: "#efefef" },
        }}
      >
        <Box sx={{ display: "grid", gridTemplateColumns: driverColumns, minWidth: 460 }}>
          <SortHeader
            field="rank"
            activeField={sortState.field}
            direction={sortState.direction}
            onClick={handleSort}
            align="center"
          >
            Rank
          </SortHeader>
          <SortHeader
            field="driver"
            activeField={sortState.field}
            direction={sortState.direction}
            onClick={handleSort}
          >
            Driver
          </SortHeader>
          <SortHeader
            field="fuelEff"
            activeField={sortState.field}
            direction={sortState.direction}
            onClick={handleSort}
          >
            Fuel Eff.
          </SortHeader>
          {!isUSA && (
          <SortHeader
            field="statusLabel"
            activeField={sortState.field}
            direction={sortState.direction}
            onClick={handleSort}
            right={false}
          >
            MPG Review Status
          </SortHeader>
          )}

          {sortedRows.map((row) => {
            const isSelected = selectedDrivers.includes(row.driver);
            return (
              <React.Fragment key={row.driver}>
                <Cell align="center" bg={isSelected ? "#dbeafe" : "#f3f3f3"}>
                  <Box onClick={() => toggleSelection(row.driver, setSelectedDrivers)} sx={{ cursor: "pointer" }}>
                    {row.rank}
                  </Box>
                </Cell>

                <Cell align="left" bg={isSelected ? "#dbeafe" : "#f3f3f3"}>
                  <Box
                    onClick={() => toggleSelection(row.driver, setSelectedDrivers)}
                    title={row.driver}
                    sx={{ cursor: "pointer", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
                  >
                    {row.driver}
                  </Box>
                </Cell>

                <Cell align="center" bg={isSelected ? "#c7defc" : getFuelEffColor(row.fuelEff)}>
                  <Box onClick={() => toggleSelection(row.driver, setSelectedDrivers)} sx={{ cursor: "pointer" }}>
                    {n2.format(convertEfficiencyFromMpg(row.fuelEff, unitSystem))}
                  </Box>
                </Cell>

                {!isUSA && (
                <Cell align="left" right={false} bg={isSelected ? "#dbeafe" : statusBg(row.statusCode)} color={statusColor(row.statusCode)}>
                  <Box
                    onClick={() => toggleSelection(row.driver, setSelectedDrivers)}
                    title={row.statusLabel}
                    sx={{ cursor: "pointer", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
                  >
                    {row.statusLabel}
                  </Box>
                </Cell>
                )}
              </React.Fragment>
            );
          })}
        </Box>
      </Box>
    </Box>
  );
}

function TopChart({ data = [], unitSystem = "imperial" }) {
  const chartWidth = Math.max(data.length * 52, 1200);

  const chartData = data.map((item) => ({
    ...item,
    fuelEffDisplay: convertEfficiencyFromMpg(item.fuelEff, unitSystem),
  }));

  const targetDisplay = convertEfficiencyFromMpg(TARGET_MPG, unitSystem);

  return (
    <Paper sx={{ borderRadius: 0, border: "1px solid #d5d5d5", bgcolor: "#efefef", p: 0.5, height: 225, overflow: "hidden" }}>
      <Box sx={{ width: "100%", height: "100%", overflowX: "auto", overflowY: "hidden" }}>
        <Box sx={{ width: chartWidth, height: "100%" }}>
          <BarChart width={chartWidth} height={210} data={chartData} margin={{ top: 24, right: 18, left: 10, bottom: 48 }}>
            <CartesianGrid strokeDasharray="2 4" vertical={false} />
            <XAxis dataKey="driver" angle={-90} textAnchor="end" interval={0} height={65} tick={{ fontSize: 11 }} />
            <YAxis tick={{ fontSize: 12 }} />

            <Tooltip
              formatter={(value) => [
                Number(value).toFixed(2),
                `Fuel Eff. ${efficiencyLabel(unitSystem)}`
              ]}
            />

            <ReferenceLine
              y={targetDisplay}
              stroke="#15b826"
              strokeWidth={2}
              label={{
                value: `${efficiencyLabel(unitSystem)} Goal ${targetDisplay.toFixed(2)}`,
                position: "insideTopLeft",
                fill: "#15b826",
                fontSize: 12,
              }}
            />

            <Bar dataKey="fuelEffDisplay" radius={[2, 2, 0, 0]}>
              {chartData.map((entry, index) => (
                <RechartsCell key={index} fill={getFuelEffColor(entry.fuelEff)} />
              ))}
            </Bar>
          </BarChart>
        </Box>
      </Box>
    </Paper>
  );
}

export default function MasterDashboardDriverPowerBI() {
  
  const navigate = useNavigate();
  const location = useLocation();  

  const dataFromMain = Array.isArray(location.state?.data) ? location.state.data : [];
  const initialUnitSystem = location.state?.unitSystem || "imperial";

  const countryMode = location.state?.countryMode || "MEX";
  const isUSA = countryMode === "USA";

  const [unitSystem, setUnitSystem] = useState(initialUnitSystem);

  const [data, setData] = useState(dataFromMain);
  const [loading, setLoading] = useState(false);

  const [selectedDrivers, setSelectedDrivers] = useState([]);
  const [selectedCompanies, setSelectedCompanies] = useState([]);
  const [selectedMakes, setSelectedMakes] = useState([]);
  const [search, setSearch] = useState("");
  const [driverSort, setDriverSort] = useState({ field: "rank", direction: "desc" });

  useEffect(() => {
    setData(dataFromMain);
  }, [dataFromMain]);

  const filteredData = useMemo(() => {
    return data.filter((row) => {
      const driver = String(row?.driver || "").trim();
      const company = String(
        isUSA ? row?.company : row?.Asset_trsnport_line
      ).trim() || "UNKNOWN";

      const make = String(row?.brand || row?.Make || "UNKNOWN").trim() || "UNKNOWN";

      const matchDriver = selectedDrivers.length === 0 || selectedDrivers.includes(driver);
      const matchCompany = selectedCompanies.length === 0 || selectedCompanies.includes(company);
      const matchMake = isUSA || selectedMakes.length === 0 || selectedMakes.includes(make);

      return matchDriver && matchCompany && matchMake;
    });
  }, [data, selectedDrivers, selectedCompanies, selectedMakes, isUSA]);

  const companyRanking = useMemo(
  () => buildCompanyRanking(filteredData, isUSA),
  [filteredData, isUSA]
);
const makeRanking = useMemo(() => buildMakeRanking(filteredData), [filteredData]);

  const chartData = useMemo(() => buildWeekChartData(filteredData), [filteredData]);

  const driverRanking = useMemo(() => {
    const ranking = buildDriverRanking(filteredData);

    if (!search.trim()) return ranking;

    return ranking.filter((item) =>
      item.driver.toLowerCase().includes(search.toLowerCase())
    );
  }, [filteredData, search]);

  const generalTableRows = useMemo(() => buildGeneralTableRows(filteredData), [filteredData]);

  return (
    <Box sx={{ minHeight: "100vh", bgcolor: "#ececec", p: 1 }}>

    <Box sx={{ mb: 1 }}>
      <Button
        variant="contained"
        onClick={() =>
          navigate("/diesel/kpi/dashboard", {
            state: { data, unitSystem, countryMode },
          })
        }
        sx={{
          minWidth: 96,
          height: 36,
          borderRadius: 1.5,
          textTransform: "none",
          fontWeight: 800,
          fontSize: 13,
          boxShadow: "0 3px 10px rgba(0,0,0,0.18)",
        }}
      >
        MAIN
      </Button>
    </Box>
      <Paper
        sx={{
          height: "calc(100vh - 230px)",
          bgcolor: "#ececec",
          borderRadius: 0,
          border: "1px solid #dadada",
          p: 1,
          overflow: "hidden",
        }}
      >
        {loading ? (
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", height: "100%" }}>
            <Typography sx={{ fontWeight: 700 }}>Loading driver dashboard...</Typography>
          </Box>
        ) : (
          <Box sx={{ display: "grid", gridTemplateColumns: "300px 1fr", gap: 1, height: "100%", minHeight: 0 }}>
            <Paper
              sx={{
                borderRadius: 0,
                border: "1px solid #d5d5d5",
                bgcolor: "#f8f8f8",
                p: 0.7,
                display: "grid",
                gridTemplateRows: "auto auto minmax(0, 1fr)",
                gap: 0.8,
                overflow: "hidden",
                minHeight: 0,
              }}
            >
              <RankingSection
                titleLeft="COMPANY"
                rows={companyRanking}
                rowKey="company"
                labelField="company"
                selectedValues={selectedCompanies}
                setSelectedValues={setSelectedCompanies}
                height={88}
                columns="1.35fr 105px"
                unitSystem={unitSystem}
              />

              {!isUSA && (
              <RankingSection
                titleLeft="MAKE"
                rows={makeRanking}
                rowKey="make"
                labelField="make"
                selectedValues={selectedMakes}
                setSelectedValues={setSelectedMakes}
                height={86}
                columns="1.35fr 105px"
                unitSystem={unitSystem}
              />
              )}

              <DriverRankingCard
                rows={driverRanking}
                selectedDrivers={selectedDrivers}
                setSelectedDrivers={setSelectedDrivers}
                search={search}
                setSearch={setSearch}
                sortState={driverSort}
                setSortState={setDriverSort}
                unitSystem={unitSystem}
                isUSA={isUSA}
              />
            </Paper>

            <Box sx={{ display: "grid", gridTemplateRows: "225px minmax(0, 1fr)", gap: 1, minHeight: 0 }}>
              <TopChart data={chartData} unitSystem={unitSystem} />

              <GeneralTable rows={generalTableRows} unitSystem={unitSystem} isUSA={isUSA} />
            </Box>
          </Box>
        )}
      </Paper>
    </Box>
  );
}
